import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { Reveal, Eyebrow, fadeUp } from "../components/Motion";

const FOUNDED_IMG =
  "https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

const COMMITMENT_IMG =
  "https://images.unsplash.com/photo-1426604966848-d7adac402bff?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

export default function Philosophy() {
  const { t } = useI18n();
  return (
    <div className="pt-32" data-testid="philosophy-page">
      {/* HEADER */}
      <section className="px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <Eyebrow>{t.philosophy.eyebrow}</Eyebrow>
          <motion.h1
            {...fadeUp}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tighter leading-[1.02] mt-6 max-w-5xl"
          >
            {t.philosophy.title}.
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="text-lg sm:text-xl text-[#4A4D4A] mt-8 max-w-2xl leading-relaxed"
          >
            {t.philosophy.lead}
          </motion.p>
        </div>
      </section>

      {/* IMAGE + PILLARS */}
      <section className="mt-20 sm:mt-28 px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden">
              <img
                src={FOUNDED_IMG}
                alt="Founded 2006"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 bg-gradient-to-t from-black/70 to-transparent text-white">
                <div className="text-xs tracking-[0.22em] uppercase text-white/70">
                  Founded
                </div>
                <div className="font-display text-5xl tracking-tighter mt-1">
                  2006
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 space-y-8">
            <p className="text-base sm:text-lg text-[#4A4D4A] leading-relaxed">
              {t.philosophy.p1}
            </p>
            <p className="text-base sm:text-lg text-[#4A4D4A] leading-relaxed">
              {t.philosophy.p2}
            </p>
            <div className="pt-4">
              <Eyebrow color="green">{t.philosophy.pillarsTitle}</Eyebrow>
            </div>
            <ul className="space-y-5">
              {t.philosophy.pillars.map((p, i) => (
                <li
                  key={p.t}
                  className="flex gap-5 pb-5 border-b border-[#1A1A1A]/10 last:border-none"
                >
                  <span className="font-mono text-xs text-[#A6192E] mt-1 tracking-wider min-w-[28px]">
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-[#1A1A1A]">
                      {p.t}
                    </h3>
                    <p className="text-sm text-[#4A4D4A] mt-1.5 leading-relaxed">
                      {p.d}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* COMMITMENT IMAGE BAND */}
      <section className="mt-32 px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <div className="relative aspect-[16/9] sm:aspect-[21/9] rounded-sm overflow-hidden">
            <img
              src={COMMITMENT_IMG}
              alt="Long-term ownership across generations"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 sm:px-14 lg:px-20 max-w-2xl text-white">
                <Eyebrow color="red" className="text-[#E0A6A6]">
                  Our commitment
                </Eyebrow>
                <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-5 leading-[1.05]">
                  Patient capital. <br />
                  <span className="text-[#E0A6A6]">Lasting companies.</span>
                </h2>
                <p className="text-base sm:text-lg text-white/85 mt-6 max-w-lg leading-relaxed">
                  We believe long-term ownership creates better businesses. By
                  aligning interests, fostering innovation, and taking
                  responsibility beyond financial metrics, we generate lasting
                  value — for our companies, partners, and future generations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="mt-24 sm:mt-32 px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Eyebrow color="green">Where we invest</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter mt-5 leading-[1.05] text-[#1A1A1A]">
              See the companies we own and develop.
            </h2>
            <p className="text-base sm:text-lg text-[#4A4D4A] mt-5 max-w-2xl leading-relaxed">
              From compressed biogas in India to plastic-recycling pioneers in
              the United States — explore the businesses through which we put
              Family Equity into practice.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/portfolio"
              data-testid="philosophy-portfolio-cta"
              className="inline-flex items-center gap-2 px-7 py-4 bg-[#A6192E] hover:bg-[#8C1527] text-white text-sm font-medium tracking-wide rounded-sm transition-all hover:-translate-y-0.5"
            >
              See portfolio companies <ArrowRight size={16} weight="bold" />
            </Link>
            <Link
              to="/approach"
              className="inline-flex items-center gap-2 px-7 py-4 border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-sm font-medium tracking-wide rounded-sm transition-all"
            >
              Our approach
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
